import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  LinearProgress,
  IconButton,
  Drawer,
  Divider,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
  Select,
  MenuItem,
  Collapse,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { ConfirmationDialog } from "../ParkingConfiguration/ConfirmationDialog";
import { deleteCpPaymentEntry } from "../../Services/CpPayout";
import { successTost, errorTost } from "../../Services/Toast";

/* ─── CONSTANTS ──────────────────────────────────────────────────────────── */
const COLORS = {
  bg: "#f5f7fb",
  border: "#e5e7eb",
  textSecondary: "#6b7280",
  blue: "#2563eb",
  blueLight: "#dbeafe",
  green: "#16a34a",
  greenLight: "#dcfce7",
  purple: "#7c3aed",
  greyBox: "#f3f4f6",
  orange: "#f59e0b",
  red: "#dc2626",
};

/* ─── HELPERS ────────────────────────────────────────────────────────────── */
const toNumber = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  const num = Number(val);
  return Number.isNaN(num) ? 0 : num;
};

const formatIndianNumber = (num = 0, decimals = 0) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(toNumber(num));

const formatCurrency = (num = 0) => `₹${formatIndianNumber(num, 0)}`;

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

// CpPayments.Status uses values like "Full Payment Done" / "Partial Payment
// Done" — those contain "payment", not "paid", so a plain .includes("paid")
// check (and the old filter logic below) missed them. Match "payment done"
// explicitly so the status chip and the drawer's filter chips agree.
const isPaymentDoneStatus = (status) => {
  const s = String(status || "").toLowerCase();
  return s.includes("paid") || s.includes("complete") || s.includes("payment done");
};

const getBookingStatusMeta = (status) => {
  const raw = status ?? "";
  const s = raw.toLowerCase().trim();
  // No CpPayments entry yet → no status to read. Show an explicit "Not Created"
  // (neutral grey) instead of a vague "Unknown".
  if (!s) return {
    label: "Not Created",
    color: COLORS.textSecondary,
    bg: COLORS.greyBox,
    border: "#d1d5db",
    dot: "#9ca3af",
  };

  if (isPaymentDoneStatus(s))
    return { label: raw, color: COLORS.green, bg: "#edfbf3", border: "#b7efd2", dot: COLORS.green };

  if (s.includes("process") || s.includes("pending") || s.includes("initiated"))
    return { label: raw, color: COLORS.blue, bg: "#eff6ff", border: "#bfdbfe", dot: COLORS.blue };

  if (s.includes("rejected") || s.includes("failed"))
    return { label: raw, color: COLORS.red, bg: "#fef2f2", border: "#fecaca", dot: COLORS.red };

  // Soft-deleted CpPayments row — visually distinct from the active/pending
  // orange bucket below, otherwise it reads as still-in-progress.
  if (s.includes("deleted"))
    return { label: raw, color: COLORS.textSecondary, bg: COLORS.greyBox, border: "#d1d5db", dot: "#9ca3af" };

  return { label: raw, color: COLORS.orange, bg: "#fff7ed", border: "#fed7aa", dot: COLORS.orange };
};

// CpPayments.Status becomes "Deleted" once an entry is soft-deleted.
const isDeletedStatus = (status) => String(status || "").toLowerCase().trim() === "deleted";

const getFileNameFromUrl = (url = "") => {
  try {
    const cleanUrl = url.split("?")[0];
    return cleanUrl.substring(cleanUrl.lastIndexOf("/") + 1) || "Invoice";
  } catch { return "Invoice"; }
};

const handleInvoiceDownload = (url) => {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

const getFyLabel = (fyFilter) =>
  fyFilter && fyFilter !== "All" ? fyFilter : "All Financial Years";

// Only these CpPayments.Status values are eligible for the card's delete action.
const isDeletableCpPaymentStatus = (status) => {
  const s = String(status || "").toLowerCase().trim();
  return s === "pending for invoice" || s === "rejected";
};

/* ─── LOADER ─────────────────────────────────────────────────────────────── */
const TabLoader = ({ count = 3 }) => (
  <>
    {Array.from({ length: count }).map((_, idx) => (
      <Card key={idx} sx={{ p: 2, borderRadius: 2, border: `1px solid ${COLORS.border}`, mb: 2, boxShadow: "none" }}>
        <Skeleton variant="text" width="35%" height={30} />
        <Skeleton variant="text" width="25%" height={22} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {Array.from({ length: 4 }).map((__, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Skeleton variant="text" width="70%" height={20} />
              <Skeleton variant="text" width="80%" height={28} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={54} sx={{ mt: 2, borderRadius: 2 }} />
      </Card>
    ))}
  </>
);

/* ─── BOOKING CARD ───────────────────────────────────────────────────────── */
/**
 * Each row comes from the RPC (get_all_brokerage_data).
 * Fields that the RPC does NOT return yet (invoice_url, status, paid_amount, etc.)
 * are passed as null/0 from the parent and render gracefully.
 */
const BookingCard = ({ row, idx, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteNote, setDeleteNote] = useState("");
  const agreementValue = toNumber(row.agreement_value ?? row.booking_value);
  const brokerageAmount = toNumber(row.brokerage_basic ?? row.basic);
  const paid = toNumber(row.paid_amount ?? 0);
  const paymentDate = row.payment_date || null;
  const tdsAmount = toNumber(row.tds_amount ?? 0);

  const bookingStatusMeta = getBookingStatusMeta(row.status);
  const hasInvoice = !!row.invoice_url;
  const canDelete = !row._noCpPayment && isDeletableCpPaymentStatus(row.status);

  const handleConfirmDelete = async () => {
    if (!deleteNote.trim()) return;
    setDeleting(true);
    const result = await deleteCpPaymentEntry(row.allotmentPaymentId, deleteNote);
    setDeleting(false);
    setDeleteDialogOpen(false);
    setDeleteNote("");

    if (result.success) {
      successTost("CP payment entry deleted. This booking is reopened for a new CP payment entry.");
      onDeleted?.(row.allotmentPaymentId);
    } else {
      errorTost(result.message || "Failed to delete CP payment entry.");
    }
  };

  const rawBrokerage = toNumber(row.brokerage_percentage);
  const isPercent = rawBrokerage <= 100;
  const brokerageDisplay = isPercent ? `${rawBrokerage.toFixed(2)}%` : formatCurrency(rawBrokerage);

  const timelineItems = [
    ...(row.logs || row.payment_logs || []).map((log) => ({
      type: "log",
      date: log.created_at,
      title: log.Type || log.type || "Log",
      message: log.Message || log.message || "-",
    })),
    ...(row.fvPayments || []).map((fv) => ({
      type: "payment",
      date: fv.FV_payment_Date || fv.created_at,
      title: "Payment Received",
      message: `₹${formatIndianNumber(fv.Amount || 0)} | UTR: ${fv.UTR || "-"}`,
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Always-visible summary line: Project • Wing • Unit + the lead-agreement fields.
  const summaryMeta = [
    { label: "Agreement Value", value: formatCurrency(agreementValue) },
    { label: "Unit Of", value: ownershipLabel(row.unit_of) },
    { label: "Agreement Date", value: formatFlexDate(row.agreement_date) },
    { label: "Booking Date", value: formatFlexDate(row.booking_date) },
  ];

  return (
    <Card sx={{ borderRadius: 2, border: `1px solid ${COLORS.border}`, mb: 2, boxShadow: "none", overflow: "hidden" }}>
      {/* Summary header — click to expand full details */}
      <Box onClick={() => setOpen((o) => !o)} sx={{ p: 2, cursor: "pointer", "&:hover": { bgcolor: "#fafbfc" } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Typography fontWeight={700} fontSize={16} sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {[row.project_short_code, row.wing_name, row.unit_no]
              .filter((p) => p !== null && p !== undefined && p !== "")
              .join(" • ") || `Booking #${idx + 1}`}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
            <Box
              sx={{
                display: "flex", alignItems: "center", gap: 0.6,
                background: bookingStatusMeta.bg, border: `1px solid ${bookingStatusMeta.border}`,
                borderRadius: 10, px: 1.2, py: 0.4, whiteSpace: "nowrap",
              }}
            >
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: bookingStatusMeta.dot }} />
              <Typography fontSize={13} fontWeight={600} color={bookingStatusMeta.color}>
                {bookingStatusMeta.label}
              </Typography>
            </Box>
            {canDelete && (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setDeleteDialogOpen(true); }}
                sx={{ color: COLORS.red, border: `1px solid ${COLORS.border}`, borderRadius: 1.5, p: 0.6, "&:hover": { bgcolor: "#fef2f2", borderColor: COLORS.red } }}
              >
                <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            <ExpandMoreIcon sx={{ color: COLORS.textSecondary, transition: "transform .2s", transform: open ? "rotate(180deg)" : "none" }} />
          </Box>
        </Box>

        {/* Lead-agreement summary fields */}
        <Grid container spacing={1.5} mt={0.25}>
          {summaryMeta.map(({ label, value }) => (
            <Grid item xs={6} sm={3} key={label}>
              <Typography fontSize={12} color={COLORS.textSecondary}>{label}</Typography>
              <Typography fontWeight={600} fontSize={13.5}>{value}</Typography>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Full details — revealed by the dropdown */}
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 2, pb: 2 }}>
          <Divider sx={{ mb: 2 }} />

          {/* Brokerage Info */}
          <Grid container spacing={2}>
            {[
              { label: isPercent ? "Brokerage %" : "Brokerage Value", value: brokerageDisplay },
              { label: "Brokerage Amount", value: formatCurrency(brokerageAmount) },
            ].map(({ label, value }) => (
              <Grid item xs={6} sm={4} key={label}>
                <Typography fontSize={13} color={COLORS.textSecondary}>{label}</Typography>
                <Typography fontWeight={700} fontSize={14}>{value}</Typography>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Payment */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography fontSize={13} color={COLORS.textSecondary}>Paid Amount</Typography>
              <Typography fontWeight={700} fontSize={16} color={COLORS.green}>{formatCurrency(paid)}</Typography>
              <Typography fontSize={12} color={COLORS.textSecondary}>(TDS: {formatCurrency(tdsAmount)})</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography fontSize={13} color={COLORS.textSecondary}>Payment Date</Typography>
              <Typography fontWeight={600} fontSize={14}>
                {paymentDate ? formatDateOnly(paymentDate) : "-"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Invoice */}
          <Typography fontSize={12} fontWeight={700} letterSpacing={0.7} color={COLORS.textSecondary} sx={{ textTransform: "uppercase", mb: 1 }}>
            Attached Invoice
          </Typography>
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "#f9fafb", border: `1px solid ${COLORS.border}`,
              borderRadius: 2, px: 1.5, py: 1.2, gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: COLORS.textSecondary }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography fontSize={13} fontWeight={600} noWrap
                  title={hasInvoice ? row.invoice_number || getFileNameFromUrl(row.invoice_url) : "Invoice Not Linked Yet"}
                >
                  {hasInvoice ? row.invoice_number || getFileNameFromUrl(row.invoice_url) : "Invoice Not Linked Yet"}
                </Typography>
                <Typography fontSize={13} color={COLORS.textSecondary}>
                  {hasInvoice ? `Invoice Date: ${row.invoice_date ? formatDateOnly(row.invoice_date) : "-"}` : "No file attached"}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small" disabled={!hasInvoice}
              onClick={(e) => { e.stopPropagation(); handleInvoiceDownload(row.invoice_url); }}
              sx={{ border: `1px solid ${COLORS.border}`, bgcolor: "#fff", opacity: hasInvoice ? 1 : 0.5, "&:hover": { bgcolor: "#f3f4f6" } }}
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: 20, color: hasInvoice ? COLORS.blue : COLORS.textSecondary }} />
            </IconButton>
          </Box>

          {/* Timeline */}
          <Divider sx={{ my: 2 }} />
          <Accordion disableGutters elevation={0} sx={{ border: "none", "&:before": { display: "none" }, bgcolor: "transparent" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 48 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography fontSize={13} fontWeight={700} color="text.secondary">TIMELINE</Typography>
                <Box sx={{ background: "#f0f2f5", borderRadius: 5, px: 0.9, py: 0.1 }}>
                  <Typography fontSize={13} fontWeight={600} color="#5a6578">{timelineItems.length}</Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <Divider sx={{ mb: 1 }} />
            <AccordionDetails sx={{ px: 0, pt: 0 }}>
              {timelineItems.length === 0 ? (
                <Typography fontSize={13} color={COLORS.textSecondary}>No timeline available.</Typography>
              ) : (
                <Box sx={{ position: "relative", pl: 2 }}>
                  {timelineItems.map((item, index) => (
                    <Box key={index} sx={{ position: "relative", pl: 2.5, pb: index !== timelineItems.length - 1 ? 2.2 : 0 }}>
                      {index !== timelineItems.length - 1 && (
                        <Box sx={{ position: "absolute", left: 4, top: 10, width: "2px", height: "100%", bgcolor: "#e5e7eb" }} />
                      )}
                      <Box sx={{ position: "absolute", left: 0, top: 4, width: 10, height: 10, borderRadius: "50%", bgcolor: item.type === "payment" ? COLORS.green : COLORS.blue }} />
                      <Typography fontSize={13} fontWeight={600}>{item.title}</Typography>
                      <Typography fontSize={13} color={COLORS.textSecondary} sx={{ mb: 0.4 }}>{formatDateTime(item.date)}</Typography>
                      <Typography fontSize={13}>{item.message}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </Collapse>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => { if (!deleting) { setDeleteDialogOpen(false); setDeleteNote(""); } }}
        onConfirm={handleConfirmDelete}
        title="Delete CP Payment Entry?"
        description="This will clear the payment history for this CP payment and let you start a fresh CP payment entry for this booking. You can do this because all invoices raised against this payment have been rejected."
        confirmText={deleting ? "Deleting..." : "Delete"}
        variant="destructive"
        confirmDisabled={deleting || !deleteNote.trim()}
      >
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label="Reason for deleting"
          placeholder="Why is this CP payment entry being deleted?"
          value={deleteNote}
          onChange={(e) => setDeleteNote(e.target.value)}
          disabled={deleting}
          sx={{ mt: 2 }}
        />
      </ConfirmationDialog>
    </Card>
  );
};

/* ─── BASIC BROKERAGE TAB ────────────────────────────────────────────────── */
/* ─── BASIC TAB FIX ───────────────────────────────────────── */
const BasicBrokerageTab = ({
  bookingRows = [],
  fyFilter,
  loading = false,
  onDeleted,
}) => {
  const [filter, setFilter] = useState("all");
  // Project-wise invoice filter (this CP can have bookings across projects).
  const [projectFilter, setProjectFilter] = useState("all");

  // Distinct projects present in this CP's bookings — for the project dropdown.
  const projectOptions = useMemo(() => {
    const map = new Map();
    bookingRows.forEach((r) => {
      const id = r.project_id || "no-project";
      if (!map.has(id)) {
        map.set(id, {
          id,
          label: r.project_short_code || r.projectName || "Unknown Project",
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label))
    );
  }, [bookingRows]);

  // Rows for the selected project (before the status chip is applied). The
  // summary cards + "Showing" bar are derived from this so counts are per-project.
  const projectRows = useMemo(() => {
    if (projectFilter === "all") return bookingRows;
    return bookingRows.filter((r) => (r.project_id || "no-project") === projectFilter);
  }, [bookingRows, projectFilter]);

  const filteredRows = useMemo(() => {
    return projectRows.filter((row) => {
      const hasInvoice = !!row.invoice_url;
      const paid = toNumber(row.paid_amount ?? 0);
      const done = paid > 0 || isPaymentDoneStatus(row.status);

      // Leads with no CpPayments entry yet ("Not Created") aren't "pending
      // invoice" — they haven't even started the CP-payment flow. A deleted
      // entry (no invoice, but soft-deleted) shouldn't show here either.
      if (filter === "pending_invoice") return !row._noCpPayment && !hasInvoice && !isDeletedStatus(row.status);
      if (filter === "payment_in_process") return hasInvoice && !done;
      if (filter === "payment_done") return done;

      return true;
    });
  }, [projectRows, filter]);

  // Summary counts reflect the selected project. When "all" is selected these
  // match the parent-supplied bookingSummary (same definitions).
  const counts = useMemo(() => ({
    totalInvoices: projectRows.length,
    pendingInvoiceCount: projectRows.filter((r) => !r._noCpPayment && !r.invoice_url && !isDeletedStatus(r.status)).length,
    inProcessCount: projectRows.filter(
      (r) => r.invoice_url && !(toNumber(r.paid_amount) > 0 || isPaymentDoneStatus(r.status))
    ).length,
    paymentDoneCount: projectRows.filter(
      (r) => toNumber(r.paid_amount) > 0 || isPaymentDoneStatus(r.status)
    ).length,
  }), [projectRows]);

  const totalInvoices = counts.totalInvoices;
  const pendingInvoiceCount = counts.pendingInvoiceCount;
  const inProcessCount = counts.inProcessCount;
  const paymentDoneCount = counts.paymentDoneCount;

  const filteredValue = filteredRows.reduce(
    (sum, item) => sum + toNumber(item.agreement_value || item.booking_value),
    0
  );

  return (
    <>
      {/* Summary mini cards */}
      <Grid container spacing={2} mb={2}>
        {[
          { label: "TOTAL INVOICES", value: totalInvoices, color: "#1f2937", bg: "#f3f4f6", border: "#d1d5db" },
          { label: "PENDING INVOICE", value: pendingInvoiceCount, color: "#f59e0b", bg: "#fff7ed", border: "#fed7aa" },
          { label: "IN PROCESS", value: inProcessCount, color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
          { label: "PAYMENT DONE", value: paymentDoneCount, color: "#16a34a", bg: "#ecfdf5", border: "#bbf7d0" },
        ].map((item) => (
          <Grid item xs={6} sm={3} key={item.label}>
            <Card sx={{ p: 2, borderRadius: 2, border: `1px solid ${item.border}`, bgcolor: item.bg, boxShadow: "none", textAlign: "center" }}>
              <Typography fontSize={12} color={COLORS.textSecondary} fontWeight={600}>{item.label}</Typography>
              <Typography fontSize={28} fontWeight={700} color={item.color} mt={0.5}>{item.value}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Project-wise filter — check invoices for one project at a time */}
      {projectOptions.length > 1 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
          <Typography fontSize={13} fontWeight={700} color={COLORS.textSecondary}>
            Project
          </Typography>
          <Select
            size="small"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            sx={{
              minWidth: 220,
              fontSize: 14,
              bgcolor: "#fff",
              borderRadius: 2,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.border },
            }}
            MenuProps={{ PaperProps: { sx: { "& .MuiMenuItem-root": { fontSize: 14 } } } }}
          >
            <MenuItem value="all" sx={{ fontSize: 14 }}>All Projects ({bookingRows.length})</MenuItem>
            {projectOptions.map((p) => (
              <MenuItem key={p.id} value={p.id} sx={{ fontSize: 14 }}>{p.label}</MenuItem>
            ))}
          </Select>
        </Box>
      )}

      {/* Filter chips */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2 }}>
        {[
          { label: "All", value: "all" },
          { label: "Pending For Invoice", value: "pending_invoice" },
          { label: "Payment In Process", value: "payment_in_process" },
          { label: "Payment Done", value: "payment_done" },
        ].map((chip) => {
          const active = filter === chip.value;
          return (
            <Box
              key={chip.value}
              onClick={() => setFilter(chip.value)}
              sx={{
                cursor: "pointer", px: 2, py: 0.9, borderRadius: "999px",
                fontSize: 13, fontWeight: 600,
                bgcolor: active ? "#1e3a8a" : "#f3f4f6",
                color: active ? "#fff" : "#6b7280",
                border: active ? "none" : `1px solid ${COLORS.border}`,
                userSelect: "none",
              }}
            >
              {chip.label}
            </Box>
          );
        })}
      </Box>

      {/* Showing bar */}
      <Box sx={{ bgcolor: "#eef1f5", borderRadius: 2, px: 2, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography fontSize={14} color={COLORS.textSecondary}>Showing</Typography>
        <Typography fontSize={14} fontWeight={700}>
          {formatIndianNumber(filteredRows.length)} Units • {formatCurrency(filteredValue)}
        </Typography>
      </Box>

      {loading ? (
        <TabLoader count={3} />
      ) : filteredRows.length === 0 ? (
        <Card sx={{ p: 2, borderRadius: 2, border: `1px solid ${COLORS.border}` }}>
          <Typography fontSize={14} color={COLORS.textSecondary}>No booking data found.</Typography>
        </Card>
      ) : (
        filteredRows.map((row, idx) => (
          <BookingCard key={row.id || row.allotmentPaymentId || idx} row={row} idx={idx} onDeleted={onDeleted} />
        ))
      )}
    </>
  );
};

/* ─── AOP / POP PLAN ACCORDION LIST ─────────────────────────────────────── */
const PlanAccordionList = ({ plans = [], accentColor, emptyMessage, loading = false, type = "AOP" }) => {
  if (loading) return <TabLoader count={3} />;

  if (plans.length === 0) {
    return (
      <Card sx={{ p: 2, borderRadius: 2, border: `1px solid ${COLORS.border}` }}>
        <Typography fontSize={14} color={COLORS.textSecondary}>{emptyMessage}</Typography>
      </Card>
    );
  }

  return (
    <>
      {plans.map((plan, i) => (
        <Accordion
          key={plan.aop_plan_id || plan.pop_plan_id || i}
          disableGutters elevation={0}
          sx={{ mb: 2, border: `1px solid ${COLORS.border}`, borderRadius: 2, overflow: "hidden", "&:before": { display: "none" }, boxShadow: "none" }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ bgcolor: accentColor === COLORS.blue ? "#f8fbff" : "#faf5ff", px: 2, py: 1 }}
          >
            <Box sx={{ width: "100%" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                <Box>
                  <Typography fontWeight={700} fontSize={15}>{plan.planName}</Typography>
                  <Typography fontSize={13} color={COLORS.textSecondary}>
                    {formatIndianNumber(plan.projects.length)} Projects
                  </Typography>
                </Box>
                <Box textAlign="right" mr={2}>
                  <Typography fontSize={13} color={COLORS.textSecondary}>Total Achieved</Typography>
                  <Typography fontWeight={700} fontSize={15}>{formatCurrency(plan.total)}</Typography>
                </Box>
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 2, bgcolor: "#fff" }}>
            {plan.projects.map((proj, j) => (
              <Card
                key={proj.project_id || j}
                sx={{ p: 2, borderRadius: 2, mb: j !== plan.projects.length - 1 ? 2 : 0, border: `1px solid ${COLORS.border}`, boxShadow: "none" }}
              >
                <Typography fontWeight={600} fontSize={14}>{proj.name}</Typography>
                <Grid container spacing={2} mt={1}>
                  {[
                    { label: "Sold Value", value: formatCurrency(proj.soldValue) },
                    { label: "Saleable Area", value: `${formatIndianNumber(proj.saleableArea, 2)} sq.ft` },
                    { label: type === "AOP" ? "AOP Earnings" : "POP Earnings", value: formatCurrency(proj.achieved) },
                  ].map(({ label, value }) => (
                    <Grid item xs={6} sm={4} key={label}>
                      <Typography fontSize={13} color={COLORS.textSecondary}>{label}</Typography>
                      <Typography fontWeight={600} fontSize={14}>{value}</Typography>
                    </Grid>
                  ))}
                </Grid>

                {type === "AOP" && (() => {
                  const progress = Math.min(
                    100,
                    toNumber(plan.total > 0 ? ((proj.achieved / plan.total) * 100).toFixed(2) : 0)
                  );
                  return (
                    <Box sx={{ mt: 2, position: "relative", pt: 3 }}>
                      <Box
                        sx={{
                          position: "absolute", top: 0,
                          left: `calc(${progress}% - 5px)`, transform: "translateX(-50%)",
                          bgcolor: COLORS.blue, color: "#fff", fontSize: "9px", fontWeight: 600,
                          px: 0.7, py: "2px", borderRadius: "999px", minWidth: 32, height: 18,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          zIndex: 2, whiteSpace: "nowrap", boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                        }}
                      >
                        {progress.toFixed(0)}%
                      </Box>
                      <LinearProgress
                        variant="determinate" value={progress}
                        sx={{
                          height: 8, borderRadius: 999, bgcolor: "#d1d5db", overflow: "hidden",
                          "& .MuiLinearProgress-bar": { bgcolor: COLORS.blue, borderRadius: 999 },
                        }}
                      />
                    </Box>
                  );
                })()}
              </Card>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  );
};

/* ─── BUILD GROUPED PLAN LISTS ───────────────────────────────────────────── */
/**
 * Takes flat aopProjectRows / popProjectRows (derived in CpPayout from RPC records)
 * and groups them into plan → projects hierarchy for the accordion.
 *
 * Row shape expected (from CpPayout drawerData memo):
 * {
 *   aop_plan_id / pop_plan_id,
 *   aopPlanName / popPlanName,
 *   project_id, projectName,
 *   amount, sold_value, saleable_area, target_value
 * }
 */
/* ─── GROUPING FIX ───────────────────────────────────────── */
const buildGroupedPlans = (projectRows = [], planIdKey, planNameKey, accentColor) =>
  console.log("🚀 ~ file: CpPayoutDrawerComponent.jsx:490 ~ buildGroupedPlans ~ projectRows:", projectRows) ||
  Object.values(
    (projectRows || []).reduce((acc, row) => {
      const planKey = row?.[planIdKey] || "no-plan";

      if (!acc[planKey]) {
        acc[planKey] = {
          [planIdKey]: row?.[planIdKey] || null,
          planName: row?.[planNameKey] || "Unnamed Plan",
          total: 0,
          projectsMap: {},
        };
      }

      acc[planKey].total += toNumber(row?.amount ?? 0);

      const projectKey = row?.project_id || "no-project";

      if (!acc[planKey].projectsMap[projectKey]) {
        acc[planKey].projectsMap[projectKey] = {
          project_id: row?.project_id,
          name: row?.projectName || "Unknown Project",
          achieved: toNumber(row?.amount ?? 0),
          soldValue: toNumber(row?.sold_value ?? 0),
          saleableArea: toNumber(
            row?.saleable_area ??
            row?.saleableArea ??
            row?.area ??
            0
          ),
        };
      } else {
        const e = acc[planKey].projectsMap[projectKey];
        e.achieved += toNumber(row?.amount ?? 0);
        e.soldValue += toNumber(row?.sold_value ?? 0);
        e.saleableArea += toNumber(row?.saleable_area ?? 0);
      }

      return acc;
    }, {})
  ).map((plan) => ({
    ...plan,
    projects: Object.values(plan.projectsMap),
  }));

/* ─── SHARED HELPERS (unit ownership + flexible date) ────────────────────── */
// Used by the Basic Brokerage booking cards' summary line.
const ownershipLabel = (unitOf) => {
  const raw = unitOf == null || unitOf === "" ? "VJ" : String(unitOf);
  return /^land/i.test(raw) ? "LO" : raw; // "Landowner" → LO, else the raw value (usually VJ)
};

// Agreement/booking date may be an ISO string, an epoch, or a "DD Month YYYY"
// string — format when parseable, otherwise show the raw value.
const formatFlexDate = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const d = new Date(typeof value === "number" || /^\d+$/.test(String(value)) ? Number(value) : value);
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  }
  return String(value);
};

/* ─── MAIN DRAWER EXPORT ─────────────────────────────────────────────────── */
const CpPayoutDrawerComponent = ({
  open,
  onClose,
  selectedCp,
  bookingRows = [],
  loading = false,
  fyFilter = "All",
  onDeleted,
  // fetchingMore, hasMore, onLoadMore kept for API compatibility but unused
  // (all data is in memory from the single RPC call)
}) => {
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (open) setTab(0);
  }, [open, selectedCp?.cp_id]);

  /* ─── DRAWER FIX ───────────────────────────────────────── */
  const groupedAOPPlans = useMemo(
    () =>
      buildGroupedPlans(
        selectedCp?.aopProjectRows || [],
        "aop_plan_id",
        "aopPlanName",
        COLORS.blue
      ),
    [selectedCp]
  );

  const groupedPOPPlans = useMemo(
    () =>
      buildGroupedPlans(
        selectedCp?.popProjectRows || [],
        "pop_plan_id",
        "popPlanName",
        COLORS.purple
      ),
    [selectedCp]
  );

  if (!selectedCp) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 950 }, maxWidth: "100%" } }}
    >
      <Box sx={{ bgcolor: "#f9fafb", height: "100%", display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <Box p={2} borderBottom={`1px solid ${COLORS.border}`} display="flex" justifyContent="space-between" alignItems="flex-start" bgcolor="#fff">
          <Box>
            <Typography fontWeight={700} fontSize={20}>{selectedCp.name || "-"}</Typography>
            <Box mt={1} display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <Typography fontSize={15} fontWeight={600}>
                Brokerage Amount: {formatCurrency(selectedCp.total)}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="large" sx={{ border: `1px solid ${COLORS.border}`, bgcolor: "#fff", "&:hover": { bgcolor: "#f3f4f6" } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box px={1.5} pt={1.5} bgcolor="#fff">
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            variant="fullWidth"
            sx={{
              bgcolor: COLORS.greyBox, p: 0.5, borderRadius: 2, minHeight: 44,
              "& .MuiTabs-indicator": { display: "none" },
            }}
          >
            {["Basic Brokerage", "AOP", "POP"].map((label, i) => (
              <Tab
                key={i} label={label}
                sx={{
                  textTransform: "none", fontSize: 14, minHeight: 38, borderRadius: 2, color: "#4b5563",
                  "&.Mui-selected": { bgcolor: "#fff", fontWeight: 700, color: "#111827" },
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Scrollable content */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2, bgcolor: "#f9fafb" }}>

          {/* ── Basic Brokerage ── */}
          {tab === 0 && (
            <BasicBrokerageTab
              bookingRows={bookingRows}
              bookingSummary={selectedCp?.bookingSummary || {}}
              fyFilter={fyFilter}
              loading={loading}
              onDeleted={onDeleted}
            />
          )}

          {/* ── AOP ── */}
          {tab === 1 && (
            <>
              <Box sx={{ bgcolor: "#e0e7ff", borderRadius: 2, p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Box>
                  <Typography fontSize={14} fontWeight={600}>Total AOP Earned</Typography>
                  <Typography fontSize={13} color={COLORS.textSecondary}>FY: {getFyLabel(fyFilter)}</Typography>
                </Box>
                <Typography fontSize={14} fontWeight={700}>
                  {formatIndianNumber(groupedAOPPlans.length)} Plans • {formatCurrency(selectedCp.aop)}
                </Typography>
              </Box>
              <PlanAccordionList plans={groupedAOPPlans} accentColor={COLORS.blue} emptyMessage="No AOP data found." loading={loading} type="AOP" />
            </>
          )}

          {/* ── POP ── */}
          {tab === 2 && (
            <>
              <Box sx={{ bgcolor: "#ede9fe", borderRadius: 2, p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Box>
                  <Typography fontSize={14} fontWeight={600}>Total POP Earned</Typography>
                  <Typography fontSize={13} color={COLORS.textSecondary}>FY: {getFyLabel(fyFilter)}</Typography>
                </Box>
                <Typography fontSize={14} fontWeight={700}>
                  {formatIndianNumber(groupedPOPPlans.length)} Plans • {formatCurrency(selectedCp.pop)}
                </Typography>
              </Box>
              <PlanAccordionList plans={groupedPOPPlans} accentColor={COLORS.purple} emptyMessage="No POP data found." loading={loading} type="POP" />
            </>
          )}

        </Box>
      </Box>
    </Drawer>
  );
};

export default CpPayoutDrawerComponent;